import React, { useEffect } from 'react';
import { DateField } from '@flos/react-ui';
import { format } from 'date-fns';
import useAppState from '../../state/appState';
import { Datepicker as DatepickerType, FormValues, FormConfig } from '@topdanmark-onlinesolutions/claim-form-types';
import { ElementValue } from '../../types/form-values';
import { ClaimFormSubmitFieldValue } from '../../types/claim-form-submit-field-value';
import { getIsValid, getValidationErrors } from '../../validators/validator-mapping';
import { SummaryRowTemplate } from '../../helpers/templates';
import { ElementValueHuman } from '../../types/element-values';
import { isRequired } from '../../helpers/isRequired';
import { getElementFromElementId } from '../../helpers/getElementFromElementId';
import { getValue } from '../values/Get';
import { getClaimFormSubmitValue } from '../../helpers/getClaimFormSubmitValue';

const Datepicker = ({ elementId, caption, placeholder, allowFuture, allowPast, helpText }: DatepickerType) => {
    const formConfig = useAppState((state) => state.formConfig);
    const formValues = useAppState((state) => state.formValues);
    const setFormValue = useAppState((state) => state.setFormValue);

    const value = Datepicker.getValueOrDefault(formValues, elementId);

    const [errorMessage, setErrorMessage] = React.useState('');

    const doValidation = () => {
        if (!formConfig) {
            return;
        }

        if (value === undefined) {
            return;
        }

        const errorMessages = getValidationErrors(formConfig, formValues, elementId);
        setErrorMessage(errorMessages[0]);
    };

    useEffect(() => {
        doValidation();
        // eslint-disable-next-line
    }, [value]);

    const isValid = Datepicker.isValidField(formConfig, formValues, elementId);

    const curDate = new Date();
    const yearsInFuture = 30;
    const maxDate = allowFuture ? new Date(`${curDate.getFullYear() + yearsInFuture}-12-31`) : curDate;
    const minDate = allowPast ? new Date(`1970-01-01`) : curDate;

    const dateChosen = getDateObject(value);

    const isRequiredField = isRequired(formConfig, elementId);

    return (
        <>
            {caption && <h6>{caption}</h6>}
            <DateField
                dateFormat="dd-MM-yyyy"
                selected={dateChosen}
                onChange={(date: Date | null) => {
                    if (!date) return;
                    setFormValue(elementId, format(date, 'dd-MM-yyyy'));
                }}
                onBlur={() => {
                    doValidation();
                }}
                onFocus={(e: any) => e.target.blur()} // Prevent keyboard on mobile - https://stackoverflow.com/a/69217777
                isValid={isValid}
                id={elementId}
                key={elementId}
                errorText={errorMessage}
                label={placeholder}
                required={isRequiredField}
                aria-label={caption}
                data-testid="datefield"
                maxDate={maxDate}
                minDate={minDate}
                wrapperClassName="u-spacing-stack-none"
                helpText={helpText}
            />
        </>
    );
};

/**
 * Get date object - first swap the format MM-DD-YYYY to YYYY-MM-DD and
 * next return a date object
 * @param value
 * @returns
 */
export const getDateObject = (value: string) => {
    if (!value) {
        return null;
    }
    if (!value.match(/^\d{2}-\d{2}-\d{4}/)) {
        throw new Error(`Datefield: The date format is not correct: ${value}`);
    }

    const dateItems = value.split('-');
    const year = dateItems[2];
    const month = dateItems[1];
    const day = dateItems[0];

    const date = new Date(year + '-' + month + '-' + day);

    return date;
};

Datepicker.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string): JSX.Element => {
    const element = getElementFromElementId(formConfig, elementId) as DatepickerType;
    const { summaryCaption } = element;

    const value = getValue(formConfig, formValues, elementId);

    // Happens when the user has not filled
    // out previous pages but somehow navigated to the resume page
    if (value === undefined) {
        return <></>;
    }

    return <SummaryRowTemplate elementId={elementId} caption={summaryCaption} value={value} />;
};

Datepicker.getValueSubmit = (formValues: FormValues, elementId: string, label: string): ClaimFormSubmitFieldValue => {
    const valueObj = Datepicker.getValue(formValues, elementId);
    const valueStr = Datepicker.getValueHuman(formValues, elementId);

    const valueId = { string: valueObj };

    return getClaimFormSubmitValue('datepicker', elementId, label, valueStr, valueId);
};

Datepicker.getValue = (formValues: FormValues, elementId: string): ElementValue => {
    return formValues[elementId];
};

Datepicker.getValueOrDefault = (formValues: FormValues, elementId: string) => {
    const value = Datepicker.getValue(formValues, elementId);
    if (value === undefined) {
        return '';
    }
    return value;
};

Datepicker.getValueHuman = (formValues: FormValues, elementId: string): ElementValueHuman => {
    const value = Datepicker.getValue(formValues, elementId);

    return value;
};

Datepicker.isValid = (formConfig: FormConfig, formValues: FormValues, elementId: string): boolean | undefined => {
    return getIsValid(formConfig, formValues, elementId);
};

Datepicker.isValidField = (
    formConfig: FormConfig | undefined,
    formValues: FormValues,
    elementId: string
): boolean | undefined => {
    if (!formConfig) {
        return;
    }

    const value = Datepicker.getValue(formValues, elementId);

    // Only do the validation IF the value is NOT undefined
    if (value === undefined) {
        return;
    }

    // When the user has not typed anything,
    // undefined is returned to prevent
    // showing red errors all over as soon
    // as he sees the form
    // if (value === '' || value === undefined) {
    //     return undefined;
    // }

    const isValid = Datepicker.isValid(formConfig, formValues, elementId);

    return isValid;
};

export default Datepicker;
